import { ArrowLeft, Eye, EyeOff, Gift } from 'lucide-react';
import type { FormEvent, ReactNode } from 'react';
import { useId, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { safeNext } from '../components/RequireAuth';
import { Button } from '../components/ui/Button';
import { ChalkUnderline } from '../components/ui/Chalk';
import { Notice } from '../components/ui/Feedback';
import { Logo } from '../layouts/AppLayout';
import { useMe, usePublicInfo, useSignIn, useSignUp } from '../hooks/useAuth';
import { cn } from '../lib/cn';

function AuthShell({ title, subtitle, children, footer }: { title: string; subtitle: ReactNode; children: ReactNode; footer: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col">
      <header className="flex items-center justify-between px-5 py-5 sm:px-8">
        <Link to="/" aria-label="PR Pilot home">
          <Logo compact />
        </Link>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink">
          <ArrowLeft className="h-4 w-4" aria-hidden /> Back to home
        </Link>
      </header>
      <main id="main" className="flex flex-1 items-start justify-center px-4 pb-16 pt-6 sm:pt-12">
        <div className="w-full max-w-md">
          <h1 className="chalk-title inline-block text-5xl text-ink">
            {title}
            <ChalkUnderline color="yellow" className="-mt-0.5" />
          </h1>
          <p className="mt-3 text-sm text-muted">{subtitle}</p>
          <div className="card mt-6 p-6">{children}</div>
          <p className="mt-5 text-center text-sm text-muted">{footer}</p>
        </div>
      </main>
    </div>
  );
}

function Field({
  label,
  type = 'text',
  value,
  onChange,
  autoComplete,
  error,
  hint,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: string;
  error?: string;
  hint?: string;
}) {
  const id = useId();
  const [show, setShow] = useState(false);
  const isPassword = type === 'password';
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium">
        {label}
      </label>
      <div className="relative mt-1.5">
        <input
          id={id}
          type={isPassword && show ? 'text' : type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-err` : hint ? `${id}-hint` : undefined}
          className={cn('input', isPassword && 'pr-10', error && 'border-danger')}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-faint hover:text-ink"
            aria-label={show ? 'Hide password' : 'Show password'}
          >
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
      </div>
      {error ? (
        <p id={`${id}-err`} className="mt-1.5 text-xs text-danger" role="alert">
          {error}
        </p>
      ) : (
        hint && (
          <p id={`${id}-hint`} className="mt-1.5 text-xs text-faint">
            {hint}
          </p>
        )
      )}
    </div>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SignInPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);
  const signIn = useSignIn();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));

  if (me) return <Navigate to={next} replace />;

  const errors = {
    email: !EMAIL_RE.test(email.trim()) ? 'Enter a valid email address.' : undefined,
    password: !password ? 'Enter your password.' : undefined,
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (errors.email || errors.password) return;
    signIn.mutate({ email: email.trim(), password }, { onSuccess: () => navigate(next, { replace: true }) });
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to review Pull Requests and pick up where you left off."
      footer={
        <>
          New to PR Pilot?{' '}
          <Link to={`/signup${params.get('next') ? `?next=${encodeURIComponent(next)}` : ''}`} className="font-medium text-chalk-yellow hover:underline">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-5">
        <Field label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" error={touched ? errors.email : undefined} />
        <Field
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          error={touched ? errors.password : undefined}
        />
        {signIn.error && <Notice tone="danger">{signIn.error.message}</Notice>}
        <Button type="submit" variant="primary" className="w-full" loading={signIn.isPending}>
          Sign in
        </Button>
      </form>
    </AuthShell>
  );
}

export function SignUpPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);
  const signUp = useSignUp();
  const { data: me } = useMe();
  const { data: info } = usePublicInfo();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));

  if (me) return <Navigate to={next} replace />;

  const errors = {
    name: !name.trim() ? 'Enter your name.' : undefined,
    email: !EMAIL_RE.test(email.trim()) ? 'Enter a valid email address.' : undefined,
    password: password.length < 8 ? 'Use at least 8 characters.' : undefined,
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (errors.name || errors.email || errors.password) return;
    signUp.mutate({ name: name.trim(), email: email.trim(), password }, { onSuccess: () => navigate(next, { replace: true }) });
  };

  return (
    <AuthShell
      title="Create your account"
      subtitle="Review your first Pull Request in under a minute."
      footer={
        <>
          Already have an account?{' '}
          <Link to={`/signin${params.get('next') ? `?next=${encodeURIComponent(next)}` : ''}`} className="font-medium text-chalk-yellow hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      {info && info.signupFreeCredits > 0 && (
        <div className="mb-5 flex items-center gap-3 rounded-lg border border-dashed border-chalk-green/50 bg-chalk-green/10 px-3.5 py-2.5 text-sm">
          <Gift className="h-4 w-4 shrink-0 text-chalk-green" aria-hidden />
          <span>
            New accounts start with <strong className="text-chalk-green">{info.signupFreeCredits} free review credits</strong>.
          </span>
        </div>
      )}
      <form onSubmit={onSubmit} noValidate className="space-y-5">
        <Field label="Name" value={name} onChange={setName} autoComplete="name" error={touched ? errors.name : undefined} />
        <Field label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" error={touched ? errors.email : undefined} />
        <Field
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          error={touched ? errors.password : undefined}
          hint="At least 8 characters."
        />
        {signUp.error && <Notice tone="danger">{signUp.error.message}</Notice>}
        <Button type="submit" variant="primary" className="w-full" loading={signUp.isPending}>
          Create account
        </Button>
        <p className="text-center text-xs text-faint">We store a salted hash of your password, never the password itself.</p>
      </form>
    </AuthShell>
  );
}
